import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import LinkIcon from "@mui/icons-material/Link";
import LaunchIcon from "@mui/icons-material/Launch";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import { CAMPAIGN_STATUS_LABELS } from "../common/StatusChips";

import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  AssociatedCampaignWithRelations,
  useDeleteAssociatedCampaignMutation,
  useUpdateAssociatedCampaignStatusMutation,
  useFixAssociatedCampaignSyncMutation,
} from "../../store/apis/briefingApi";
import { resolveBriefingPlatformIcon } from "../../utils/iconUtils";
import { middleClickOpen } from "../../utils/middleClickOpen";
import ConfirmationModal from "../modals/ConfirmationModal";
import ProductionReviewDialog from "./ProductionReviewDialog";
import { useUserRole } from "../../hooks/useUserRole";
import { components } from "../../types/api";
import { useContextMenu } from "../../hooks/useContextMenu";
import { TableRowContextMenu } from "../common/TableRowContextMenu";
import AddAssociatedCampaignDialog from "./AddAssociatedCampaignDialog";
import RealInvestmentCell from "./RealInvestmentCell";
import { getCampaignStatusEditing } from "../../utils/associatedCampaignStatus";
import CampaignStatusBadge from "./CampaignStatusBadge";
import {
  formatDayRange,
  formatMonthSpan,
} from "../../utils/briefings/dateRange";

import OrbLoader from "../common/OrbLoader";
import { createNumberFormat } from "../../utils/numberFormat";
type AssociatedCampaignView = AssociatedCampaignWithRelations;

interface AssociatedCampaignsSectionProps {
  campaigns: AssociatedCampaignView[];
  isLoading: boolean;
  hasError: boolean;
  briefingId: string;
  onCampaignsChange?: () => void;
  onAddCampaign?: () => void;
  isEditMode?: boolean;
  briefingFee: number;
  briefingBudget: number;
  briefingStatus?: string;
  briefing?: components["schemas"]["BriefingDetailResponse"];
  platformOptions?: components["schemas"]["BriefingPlatform"][];
}

const AssociatedCampaignsSection: React.FC<AssociatedCampaignsSectionProps> = ({
  campaigns,
  isLoading,
  hasError,
  briefingId,
  onCampaignsChange,
  onAddCampaign,
  isEditMode = false,
  briefingFee,
  briefingBudget,
  briefingStatus,
  briefing,
  platformOptions = [],
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { role, canEditEverything } = useUserRole();
  const isTechnician = role === "TECHNICIAN";
  const isAccount = role === "ACCOUNT";
  const isAdmin = role === "ADMIN";
  // Real investment column (API value or manual entry), formerly its own table.
  const showRealColumn =
    role === "ADMIN" || role === "PRICING" || role === "TECHNICIAN";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteCampaign] = useDeleteAssociatedCampaignMutation();
  const [fixSync] = useFixAssociatedCampaignSyncMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateAssociatedCampaignStatusMutation();
  const isReadyForProduction = briefingStatus === "READY_FOR_PRODUCTION";
  const isFinalized = briefingStatus === "FINALIZED";
  const [productionDialogOpen, setProductionDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<AssociatedCampaignView | null>(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { contextMenu, handleContextMenu, handleCloseContextMenu } =
    useContextMenu<AssociatedCampaignView>();

  // Filter states
  const [filterName, setFilterName] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFunnel, setFilterFunnel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const uniquePlatforms = useMemo(() => {
    const platforms = new Set<string>();
    campaigns.forEach((c) => {
      if (c.platform) platforms.add(c.platform);
    });
    return Array.from(platforms).sort();
  }, [campaigns]);

  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    campaigns.forEach((c) => {
      if (c.briefingCampaignType) types.add(c.briefingCampaignType);
    });
    return Array.from(types).sort();
  }, [campaigns]);

  const uniqueFunnels = useMemo(() => {
    const funnels = new Set<string>();
    campaigns.forEach((c) => {
      if (c.goal?.funnel) funnels.add(c.goal.funnel);
    });
    return Array.from(funnels).sort();
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      if (
        filterName &&
        !c.name.toLowerCase().includes(filterName.toLowerCase())
      ) {
        return false;
      }
      if (filterPlatform && c.platform !== filterPlatform) return false;
      if (filterType && c.briefingCampaignType !== filterType) return false;
      if (filterFunnel && c.goal?.funnel !== filterFunnel) return false;
      if (filterStatus && (c as { status?: string }).status !== filterStatus) {
        return false;
      }
      return true;
    });
  }, [
    campaigns,
    filterName,
    filterPlatform,
    filterType,
    filterFunnel,
    filterStatus,
  ]);

  // Expanding behavior hooks for native printing

  useEffect(() => {
    const handleBeforePrint = () => {
      setExpandedRows(new Set(campaigns.map((c) => c.id)));
    };

    window.addEventListener("beforeprint", handleBeforePrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, [campaigns]);

  const numberFormatter = useMemo(
    () =>
      createNumberFormat({
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }),
    []
  );

  const formatCurrency = useMemo(
    () =>
      (value: number, currencyCode: string = "EUR") =>
        createNumberFormat({
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value),
    []
  );

  const handleView = (campaign: AssociatedCampaignView) => {
    navigate(`/briefings/${briefingId}/associated-campaigns/${campaign.id}`);
  };

  const handleDeleteClick = (campaign: AssociatedCampaignView) => {
    setSelectedCampaign(campaign);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCampaign) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteCampaign({
        briefingId,
        associatedId: selectedCampaign.id,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.deleteAssociatedSuccess",
            "Associated campaign deleted successfully"
          ),
          severity: "success",
        })
      );
      onCampaignsChange?.();
    } catch (error: unknown) {
      const errorMessage =
        (error && typeof error === "object" && "data" in error
          ? (error as { data?: { message?: string } }).data?.message
          : undefined) ??
        t(
          "briefingsView.deleteAssociatedError",
          "Failed to delete associated campaign"
        );
      dispatch(
        setToastMessage({
          message: errorMessage,
          severity: "error",
        })
      );
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setSelectedCampaign(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleDuplicateClick = (campaign: AssociatedCampaignView) => {
    setSelectedCampaign(campaign);
    setDuplicateDialogOpen(true);
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isFromInteractiveElement = (e: React.MouseEvent) =>
    !!(e.target as HTMLElement).closest(
      "button, a, input, textarea, [role='button'], [role='combobox']"
    );

  // A click on the row opens the campaign; the chevron button expands it.
  const handleRowClick = (
    e: React.MouseEvent,
    campaign: AssociatedCampaignView
  ) => {
    if (isFromInteractiveElement(e)) return;
    handleView(campaign);
  };

  const handleToggleAll = () => {
    if (expandedRows.size === campaigns.length) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(campaigns.map((c) => c.id)));
    }
  };

  const getStatusChip = (campaign: AssociatedCampaignView) => {
    const changeStatus = (
      newStatus: components["schemas"]["_36_Enums.AssociatedCampaignStatus"]
    ) => {
      updateStatus({
        briefingId,
        associatedId: campaign.id,
        status: newStatus,
      })
        .unwrap()
        .then(() => {
          dispatch(
            setToastMessage({
              message: t("common.savedSuccessfully", "Saved successfully"),
              severity: "success",
            })
          );
        })
        .catch(() => {
          dispatch(
            setToastMessage({
              message: t("common.errorSaving", "Error saving changes"),
              severity: "error",
            })
          );
        });
    };

    const editing = getCampaignStatusEditing({
      campaign,
      briefingStatus,
      role,
      canEditEverything,
      isEditMode,
    });

    return (
      <CampaignStatusBadge
        status={editing.status}
        onChange={editing.editable ? changeStatus : undefined}
        disabled={editing.editable && isUpdatingStatus}
        excludedStatuses={editing.excludedStatuses}
      />
    );
  };

  const renderKeyValueGrid = (
    entries: { label: string; value: React.ReactNode }[],
    // Grid width of each field on desktop (3 = four per row, 4 = three).
    md: number = 3
  ): React.ReactNode => {
    const filtered = entries.filter(
      (entry) =>
        entry.value !== undefined && entry.value !== null && entry.value !== ""
    );

    if (filtered.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("briefingsView.noDataAvailable", "No data available.")}
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {filtered.map(({ label, value }) => (
          <Grid item xs={12} sm={6} md={md} key={label}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {typeof value === "number"
                ? numberFormatter.format(value)
                : value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  const formatLinkValue = (
    value: string | Record<string, unknown> | undefined | null
  ): React.ReactNode => {
    if (!value) return "-";

    // Helper to render a clickable link safely
    const renderUrl = (url: string) => {
      try {
        new URL(url); // valid URL check
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: theme.palette.primary.main,
              textDecoration: "none",
            }}
          >
            {url}
          </a>
        );
      } catch {
        return url; // Return raw string if not a valid URL
      }
    };

    if (typeof value === "string") return renderUrl(value);

    // Render an object as a clean bulleted list rather than a horrible JSON string
    return (
      <Box
        component="ul"
        sx={{ m: 0, pl: 2, p: 0, listStylePosition: "inside" }}
      >
        {Object.entries(value).map(([key, val]) => (
          <Box component="li" key={key} sx={{ mb: 0.5 }}>
            <strong>{key}:</strong>{" "}
            {typeof val === "string" ? renderUrl(val) : JSON.stringify(val)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderAttachmentContent = (campaign: AssociatedCampaignView) => {
    const { goal, target, asset, sync } = campaign;
    const hasAttachments = goal || target || asset || sync;

    if (!hasAttachments) {
      return (
        <Alert severity="info">
          {t(
            "briefingsView.noAttachments",
            "No goal, target, asset or sync records attached yet."
          )}
        </Alert>
      );
    }

    // Goal and target sit side by side (stacked on small screens); a lone
    // panel takes the full width.
    const halfPanel = goal && target ? 6 : 12;

    return (
      <Stack spacing={2}>
        {(goal || target) && (
          <Grid container spacing={2}>
            {goal && (
              <Grid item xs={12} md={halfPanel}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    height: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t("briefingsView.goalDetails", "Goal details")}
                  </Typography>
                  {renderKeyValueGrid(
                    [
                      {
                        label: t("briefingsView.funnel", "Funnel"),
                        value: goal.funnel,
                      },
                      {
                        label: t("briefingsCreate.form.fee", "Fee"),
                        value: !isTechnician ? briefingFee : undefined,
                      },
                      {
                        label: t(
                          "briefingsCreate.form.totalBudget",
                          "Total budget"
                        ),
                        value: !isTechnician ? briefingBudget : undefined,
                      },
                      {
                        label: t(
                          "briefingsView.expectedVolume",
                          "Expected volume"
                        ),
                        value: goal.expectedVolume,
                      },
                      {
                        label: t("briefingsView.visibility", "Visibility"),
                        value: goal.visibility,
                      },
                      {
                        label: t("briefingsView.netUnitCost", "Net unit cost"),
                        value: !isAccount ? goal.netUnitCost : undefined,
                      },
                      {
                        label: t(
                          "briefingsView.totalInversion",
                          "Total inversion"
                        ),
                        value: !isTechnician ? goal.totalInversion : undefined,
                      },
                      {
                        label: t(
                          "briefingsView.platformInversion",
                          "Platform inversion"
                        ),
                        value: isTechnician
                          ? goal.platformInversion
                          : undefined,
                      },
                      {
                        label: t("briefingsView.margin", "Margin"),
                        value:
                          !isTechnician && !isAccount ? goal.margin : undefined,
                      },
                      {
                        label: t("briefingsView.status", "Status"),
                        value: goal.status,
                      },
                    ],
                    halfPanel === 6 ? 4 : 3
                  )}
                  {goal.notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("common.notes", "Notes")}:</strong>{" "}
                      {goal.notes}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {target && (
              <Grid item xs={12} md={halfPanel}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    height: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t("briefingsView.targetDetails", "Target details")}
                  </Typography>
                  {renderKeyValueGrid(
                    [
                      {
                        label: t("briefingsView.sex", "Sex"),
                        value: target.sex,
                      },
                      {
                        label: t("briefingsView.ageRange", "Age range"),
                        value: `${target.minimumAge}-${target.maximumAge}`,
                      },
                      {
                        label: t("briefingsView.language", "Language"),
                        value: target.language,
                      },
                      {
                        label: t("briefingsView.geography", "Geography"),
                        value: target.geography,
                      },
                      {
                        label: t("briefingsView.technology", "Technology"),
                        value: target.technology,
                      },
                      {
                        label: t("briefingsView.interests", "Interests"),
                        value: target.interests,
                      },
                      {
                        label: t("briefingsView.dataAudience", "Data audience"),
                        value: target.dataAudience,
                      },
                    ],
                    halfPanel === 6 ? 4 : 3
                  )}
                  {target.notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("common.notes", "Notes")}:</strong>{" "}
                      {target.notes}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {asset && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("briefingsView.assetDetails", "Asset details")}
            </Typography>
            {renderKeyValueGrid([
              ...(asset.destinations ?? []).flatMap(
                (destination, index, list) => {
                  const suffix = [
                    list.length > 1 ? ` ${index + 1}` : "",
                    destination.label ? ` · ${destination.label}` : "",
                  ].join("");

                  return [
                    {
                      label: `${t(
                        "briefingsView.destinationUrl",
                        "Destination URL"
                      )}${suffix}`,
                      value: destination.url,
                    },
                    {
                      label: `${t("briefingsView.utm", "UTM")}${suffix}`,
                      value: destination.utm,
                    },
                  ];
                }
              ),
              {
                label: t("briefingsView.assetsLinks", "Assets links"),
                value: formatLinkValue(asset.assetsLinks),
              },
              {
                label: t("briefingsView.copiesLinks", "Copies links"),
                value: formatLinkValue(asset.copiesLinks),
              },
              {
                label: t("briefingsView.additionalLinks", "Additional links"),
                value: formatLinkValue(asset.additionalLinks),
              },
            ])}
          </Box>
        )}

        {sync && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">
                {t("briefingsView.syncDetails", "Sync details")}
              </Typography>
              <Chip
                size="small"
                color={sync.isLinked ? "success" : "default"}
                label={
                  sync.isLinked
                    ? t("briefingsView.linked", "Linked")
                    : t("briefingsView.notLinked", "Not linked")
                }
              />
            </Stack>
            {renderKeyValueGrid([
              {
                label: t(
                  "briefingsView.beapoloCampaign",
                  "BeApolo campaign ID"
                ),
                value: sync.beapoloCampaignId,
              },
              {
                label: t(
                  "briefingsView.internalCampaign",
                  "Internal campaign ID"
                ),
                value: sync.internalCampaignId,
              },
              {
                label: t("briefingsView.insertionOrder", "Insertion order ID"),
                value: sync.insertionOrderId,
              },
            ])}
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700} sx={{}}>
          {t("briefingsView.associatedCampaigns", "Associated campaigns")}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ "@media print": { display: "none" } }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={
              expandedRows.size === campaigns.length ? (
                <UnfoldLessIcon />
              ) : (
                <UnfoldMoreIcon />
              )
            }
            onClick={handleToggleAll}
            disabled={campaigns.length === 0}
            sx={{
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: "fit-content",
            }}
          >
            {expandedRows.size === campaigns.length
              ? t("common.collapseAll", "Collapse all")
              : t("common.expandAll", "Expand all")}
          </Button>

          {onAddCampaign && (
            <Tooltip
              title={t(
                "briefingsView.addAssociatedCampaign",
                "Add associated campaign"
              )}
            >
              <IconButton
                color="primary"
                onClick={onAddCampaign}
                sx={{
                  bgcolor: "action.hover",
                  flexShrink: 0,
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {isReadyForProduction &&
            campaigns.some(
              (c) => (c as { status?: string }).status !== "READY"
            ) && (
              <Button
                variant="contained"
                color="success"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setProductionDialogOpen(true)}
                sx={{
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: "fit-content",
                }}
              >
                {t("briefingsView.createCampaigns", "Create campaigns")}
              </Button>
            )}
        </Stack>
      </Stack>
      {hasError ? (
        <Alert severity="error">
          {t(
            "briefingsView.associatedCampaignsError",
            "Unable to load associated campaigns."
          )}
        </Alert>
      ) : isLoading ? (
        <Stack direction="row" justifyContent="center" py={4}>
          <OrbLoader size="medium" />
        </Stack>
      ) : campaigns.length === 0 ? (
        <Alert severity="info">
          {t(
            "briefingsView.noAssociatedCampaigns",
            "No associated campaigns have been added yet."
          )}
        </Alert>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2, "@media print": { display: "none" } }}
          >
            <TextField
              size="small"
              placeholder={t("common.searchName", "Search by name")}
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              label={t("briefingsView.platform", "Platform")}
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>{t("common.all", "All")}</em>
              </MenuItem>
              {uniquePlatforms.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  {platform}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t("briefingsView.level", "Type")}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>{t("common.all", "All")}</em>
              </MenuItem>
              {uniqueTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t("briefingsView.goalForm.funnel", "Funnel")}
              value={filterFunnel}
              onChange={(e) => setFilterFunnel(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>{t("common.all", "All")}</em>
              </MenuItem>
              {uniqueFunnels.map((funnel) => (
                <MenuItem key={funnel} value={funnel}>
                  {funnel}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t("briefingsView.status", "Status")}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>{t("common.all", "All")}</em>
              </MenuItem>
              {Object.entries(CAMPAIGN_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {filteredCampaigns.length === 0 ? (
            <Alert severity="info">
              {t(
                "common.noResultsFound",
                "No results found matching your filters."
              )}
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "background.paper",
                        zIndex: 2,
                      }}
                    >
                      {t("common.name", "Name")}
                    </TableCell>
                    <TableCell>
                      {t("briefingsByCompany.table.dates", "Dates")}
                    </TableCell>
                    <TableCell>
                      {t("briefingsView.platform", "Platform")}
                    </TableCell>
                    <TableCell>{t("briefingsView.level", "Type")}</TableCell>
                    <TableCell align="center">
                      {t("briefingsView.status", "Status")}
                    </TableCell>
                    <TableCell>
                      {t("briefingsView.addedOn", "Added on")}
                    </TableCell>
                    <TableCell>
                      {t("briefingsView.linkedCampaign", "Linked campaign")}
                    </TableCell>
                    {isTechnician && (
                      <>
                        <TableCell>
                          {t(
                            "briefingsView.platformInversion",
                            "Platform inversion"
                          )}
                        </TableCell>
                        <TableCell>
                          {t("briefingsView.grossUnitCost", "Gross unit cost")}
                        </TableCell>
                        <TableCell>
                          {t("briefingsView.expectedVolume", "Expected volume")}
                        </TableCell>
                      </>
                    )}
                    {!isTechnician && (
                      <TableCell>
                        {t(
                          "briefingsView.campaignInvestment",
                          "Actual Inversion"
                        )}
                      </TableCell>
                    )}
                    {!isTechnician && (
                      <TableCell>
                        {t(
                          "briefingsView.totalInvestment",
                          "Planned Inversion"
                        )}
                      </TableCell>
                    )}
                    {!isTechnician && !isAccount && (
                      <TableCell>
                        {t("briefingsView.marginPercent", "Margin (%)")}
                      </TableCell>
                    )}
                    {!isTechnician && (
                      <TableCell>{t("briefingsView.fee", "Fee")}</TableCell>
                    )}
                    {showRealColumn && (
                      <TableCell>
                        {t("briefingsView.realInvestment", "Real")}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCampaigns.map((campaign) => {
                    const isExpanded = expandedRows.has(campaign.id);
                    return (
                      <React.Fragment key={campaign.id}>
                        <TableRow
                          hover={!isExpanded}
                          onClick={(e) => handleRowClick(e, campaign)}
                          {...middleClickOpen(
                            `/briefings/${briefingId}/associated-campaigns/${campaign.id}`
                          )}
                          sx={{
                            cursor: "pointer",
                            // Separator is drawn by the details row below.
                            "& > td": { borderBottom: "none" },
                            // The sticky Name cell is opaque, so it would hide
                            // the row hover; paint the hover tint over it.
                            ...(!isExpanded && {
                              "&:hover > td:first-of-type": {
                                backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                              },
                            }),
                            // Expanded: the row takes the same background as
                            // its details so both read as one block.
                            ...(isExpanded && {
                              "& > td": {
                                backgroundColor: "background.default",
                                borderBottom: "none",
                              },
                            }),
                          }}
                          onContextMenu={(e) => handleContextMenu(e, campaign)}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              backgroundColor: "background.paper",
                              zIndex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                              }}
                            >
                              {/* Same slot as the brand logo in the briefings
                                  list: expands the row in place. */}
                              <Tooltip
                                title={
                                  isExpanded
                                    ? t("common.collapse", "Collapse")
                                    : t("common.expand", "Expand")
                                }
                              >
                                <IconButton
                                  size="small"
                                  aria-label={
                                    isExpanded
                                      ? t("common.collapse", "Collapse")
                                      : t("common.expand", "Expand")
                                  }
                                  aria-expanded={isExpanded}
                                  onClick={() => toggleRow(campaign.id)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    flexShrink: 0,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    color: "text.secondary",
                                    "@media print": { display: "none" },
                                  }}
                                >
                                  <KeyboardArrowRightIcon
                                    sx={{
                                      fontSize: 18,
                                      transition: "transform 0.15s",
                                      transform: isExpanded
                                        ? "rotate(90deg)"
                                        : "none",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                              {campaign.name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {campaign.goal?.startDate ||
                            campaign.goal?.endDate ? (
                              <>
                                <Typography
                                  variant="body2"
                                  noWrap
                                  lineHeight={1.3}
                                >
                                  {formatDayRange(
                                    campaign.goal.startDate,
                                    campaign.goal.endDate,
                                    t
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  lineHeight={1.3}
                                  component="div"
                                >
                                  {formatMonthSpan(
                                    campaign.goal.startDate,
                                    campaign.goal.endDate,
                                    i18n.language
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box
                                component="img"
                                src={
                                  resolveBriefingPlatformIcon(campaign.platform)
                                    .iconPath
                                }
                                alt={campaign.platform ?? "platform"}
                                sx={{ width: 20, height: 20 }}
                              />
                              <Typography variant="body2">
                                {campaign.platform ?? "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="column">
                              <Typography variant="body2">
                                {campaign.campaignLevel === "INSERTIONORDER"
                                  ? t(
                                      "briefingsView.insertionOrder",
                                      "Insertion order"
                                    )
                                  : t("briefingsView.campaign", "Campaign")}
                              </Typography>
                              {campaign.briefingCampaignType && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {campaign.briefingCampaignType}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {getStatusChip(campaign)}
                          </TableCell>
                          <TableCell>
                            {dayjs(campaign.createdAt).format("YYYY-MM-DD")}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const hasInternal = !!(
                                campaign as {
                                  internalCampaignId?: string | null;
                                }
                              ).internalCampaignId;
                              const hasBeapolo =
                                !!campaign.sync?.beapoloCampaignId;
                              const isReady =
                                (campaign as { status?: string }).status ===
                                "READY";
                              const isBrokenLink =
                                isReady && !hasBeapolo && hasInternal;

                              if (isBrokenLink) {
                                return (
                                  <Tooltip
                                    title={t(
                                      "briefingsView.brokenLink",
                                      "Broken Link - Parent Campaign mapping is missing"
                                    )}
                                  >
                                    <Chip
                                      size="small"
                                      icon={<WarningAmberIcon />}
                                      label={t(
                                        "briefingsView.brokenLinkText",
                                        "Broken Link"
                                      )}
                                      color="warning"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }

                              if (hasBeapolo || hasInternal) {
                                return (
                                  <Chip
                                    size="small"
                                    icon={<LinkIcon />}
                                    label={t("briefingsView.ready", "Ready")}
                                    color="success"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const targetId =
                                        campaign.sync?.beapoloCampaignId ||
                                        (
                                          campaign as {
                                            internalCampaignId?: string | null;
                                          }
                                        ).internalCampaignId;
                                      navigate(`/campaigns/${targetId}`);
                                    }}
                                    sx={{ cursor: "pointer" }}
                                  />
                                );
                              }

                              return (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  —
                                </Typography>
                              );
                            })()}
                          </TableCell>
                          {isTechnician && (
                            <>
                              <TableCell>
                                {campaign.goal?.platformInversion != null
                                  ? formatCurrency(
                                      campaign.goal.platformInversion,
                                      campaign.goal.currency
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {campaign.goal?.grossUnitCost != null
                                  ? formatCurrency(
                                      campaign.goal.grossUnitCost,
                                      campaign.goal.currency
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {campaign.goal?.expectedVolume != null
                                  ? createNumberFormat({
                                      maximumFractionDigits: 0,
                                    }).format(campaign.goal.expectedVolume)
                                  : "-"}
                              </TableCell>
                            </>
                          )}
                          {!isTechnician && (
                            <TableCell>
                              {campaign.realMetrics?.campaignInvestment != null
                                ? formatCurrency(
                                    campaign.realMetrics.campaignInvestment,
                                    campaign.goal?.currency
                                  )
                                : campaign.goal?.totalInversion != null
                                  ? formatCurrency(
                                      campaign.goal.totalInversion,
                                      campaign.goal.currency
                                    )
                                  : "-"}
                            </TableCell>
                          )}
                          {!isTechnician && (
                            <TableCell>
                              {campaign.realMetrics?.totalInvestment != null
                                ? formatCurrency(
                                    campaign.realMetrics.totalInvestment,
                                    campaign.goal?.currency
                                  )
                                : campaign.goal?.totalInversion != null
                                  ? formatCurrency(
                                      campaign.goal.totalInversion,
                                      campaign.goal.currency
                                    )
                                  : "-"}
                            </TableCell>
                          )}
                          {!isTechnician && !isAccount && (
                            <TableCell>
                              {campaign.realMetrics?.marginPercent != null
                                ? `${numberFormatter.format(campaign.realMetrics.marginPercent)}%`
                                : campaign.goal?.margin != null
                                  ? `${numberFormatter.format(campaign.goal.margin)}%`
                                  : "-"}
                            </TableCell>
                          )}
                          {!isTechnician && (
                            <TableCell>
                              {campaign.realMetrics?.fee != null
                                ? formatCurrency(
                                    campaign.realMetrics.fee,
                                    campaign.goal?.currency
                                  )
                                : briefingFee != null
                                  ? formatCurrency(
                                      (briefingFee / 100) *
                                        (campaign.goal?.totalInversion || 0),
                                      campaign.goal?.currency
                                    )
                                  : "-"}
                            </TableCell>
                          )}
                          {showRealColumn && (
                            <TableCell>
                              <RealInvestmentCell
                                briefingId={briefingId}
                                campaign={campaign}
                                formatCurrency={formatCurrency}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={14}
                            sx={{
                              p: 0,
                              // The separator between rows lives here (under
                              // the panel when open, right under the row when
                              // closed); the main row draws no border of its own.
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  px: 2,
                                  pt: 1,
                                  pb: 3,
                                  bgcolor: "background.default",
                                }}
                              >
                                <Stack spacing={2}>
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{
                                      xs: "flex-start",
                                      sm: "center",
                                    }}
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                    >
                                      {t(
                                        "briefingsView.attachmentsTitle",
                                        "Attachments"
                                      )}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={3}
                                      alignItems="center"
                                    >
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          {t(
                                            "briefingsView.sourceId",
                                            "Source ID"
                                          )}
                                          :
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.primary"
                                        >
                                          {campaign.sourceId ?? "-"}
                                        </Typography>
                                      </Stack>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          {t(
                                            "briefingsView.investmentPayment",
                                            "Investment payment"
                                          )}
                                          :
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.primary"
                                        >
                                          {(
                                            campaign as {
                                              investmentPayment?: string | null;
                                            }
                                          ).investmentPayment === "CLIENTE"
                                            ? t(
                                                "briefingsView.paymentClient",
                                                "Client"
                                              )
                                            : (
                                                  campaign as {
                                                    investmentPayment?:
                                                      | string
                                                      | null;
                                                  }
                                                ).investmentPayment ===
                                                "TI_MEDIA_GROUP_SL"
                                              ? t(
                                                  "briefingsView.paymentTiMediaGroupSl",
                                                  "TI MEDIA GROUP SL"
                                                )
                                              : "-"}
                                        </Typography>
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "briefingsView.lastUpdated",
                                          "Last updated"
                                        )}{" "}
                                        {dayjs(campaign.updatedAt).format(
                                          "YYYY-MM-DD HH:mm"
                                        )}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                  {renderAttachmentContent(campaign)}
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      <ProductionReviewDialog
        open={productionDialogOpen}
        briefingId={briefingId}
        campaigns={campaigns}
        onClose={() => setProductionDialogOpen(false)}
        onSuccess={() => {
          setProductionDialogOpen(false);
          onCampaignsChange?.();
        }}
      />
      <ConfirmationModal
        open={isDeleteModalOpen}
        title={t(
          "briefingsView.deleteAssociatedConfirmTitle",
          "Delete associated campaign"
        )}
        message={t(
          "briefingsView.deleteAssociatedConfirmMessage",
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          { name: selectedCampaign?.name ?? "" }
        )}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        confirmLabel={t("common.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
      />
      {isDuplicateDialogOpen && selectedCampaign && (
        <AddAssociatedCampaignDialog
          open={isDuplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setSelectedCampaign(null);
          }}
          briefingId={briefingId}
          briefing={briefing}
          platformOptions={platformOptions}
          onSuccess={onCampaignsChange}
          duplicateMode
          campaignToDuplicate={selectedCampaign}
        />
      )}
      <TableRowContextMenu
        contextMenu={contextMenu}
        onClose={handleCloseContextMenu}
        actions={[
          {
            label: t("briefingsView.fixLink", "Fix Link"),
            icon: <BuildIcon fontSize="small" />,
            show:
              isAdmin &&
              !!contextMenu?.data &&
              (contextMenu.data as { status?: string }).status === "READY" &&
              !contextMenu.data.sync?.beapoloCampaignId &&
              !!(contextMenu.data as { internalCampaignId?: string | null })
                .internalCampaignId,
            onClick: () => {
              if (contextMenu?.data) {
                const id = contextMenu.data.id;
                fixSync({ briefingId, associatedId: id })
                  .unwrap()
                  .then(() => {
                    dispatch(
                      setToastMessage({
                        message: t(
                          "briefingsView.linkFixed",
                          "Link fixed successfully"
                        ),
                        severity: "success",
                      })
                    );
                    onCampaignsChange?.();
                  })
                  .catch((err) => {
                    dispatch(
                      setToastMessage({
                        message:
                          err?.data?.message ||
                          t("common.error", "Error fixing link"),
                        severity: "error",
                      })
                    );
                  });
              }
            },
          },
          {
            label: t("common.openInNewTab", "Open in new tab"),
            icon: <LaunchIcon fontSize="small" />,
            onClick: () => {
              if (contextMenu?.data)
                window.open(
                  `/briefings/${briefingId}/associated-campaigns/${contextMenu.data.id}`,
                  "_blank"
                );
            },
          },
          {
            label: t("common.view", "View"),
            icon: <VisibilityIcon fontSize="small" />,
            onClick: () => {
              if (contextMenu?.data) handleView(contextMenu.data);
            },
          },
          {
            label: t(
              "briefingsView.editAssociated",
              "Edit associated campaign"
            ),
            icon: <EditIcon fontSize="small" />,
            show:
              (isEditMode || (isTechnician && isReadyForProduction)) &&
              !isFinalized,
            onClick: () => {
              if (contextMenu?.data)
                navigate(
                  `/briefings/${briefingId}/associated-campaigns/${contextMenu.data.id}/edit`
                );
            },
          },
          {
            label: t(
              "briefingsView.duplicateAssociated",
              "Duplicate associated campaign"
            ),
            icon: <LibraryAddIcon fontSize="small" />,
            show: isEditMode && !isFinalized,
            onClick: () => {
              if (contextMenu?.data) handleDuplicateClick(contextMenu.data);
            },
          },
          {
            label: t("briefingsView.deleteAssociated", "Delete"),
            icon: <DeleteIcon fontSize="small" color="error" />,
            color: "error.main",
            show: isEditMode && !isFinalized,
            onClick: () => {
              if (contextMenu?.data) handleDeleteClick(contextMenu.data);
            },
          },
        ]}
      />
    </Paper>
  );
};

export default AssociatedCampaignsSection;
